import { apiFetch } from './http';
import type { A2uiSurface } from '@/types/a2ui';
import { createCatalogSurface } from '@/components/A2UIRenderer/catalogs';

export interface AgentInvokeResult {
  response: string;
  ui?: A2uiSurface;
}

/** Invokes the AgentCore-backed assistant with a single user message. */
export async function invokeAgent(message: string): Promise<AgentInvokeResult> {
  const result = await apiFetch<AgentInvokeResult>('/api/agent/invoke', {
    method: 'POST',
    body: JSON.stringify({ message }),
  });
  return normalizeAgentResult(result, message);
}

function normalizeAgentResult(result: AgentInvokeResult, prompt: string): AgentInvokeResult {
  const streamedResult = extractStreamedResult(result.response);
  const response = streamedResult.response;
  const delimitedUi = extractDelimitedUi(response);
  const greetingUi = isBareGreeting(prompt) ? createCatalogSurface('WelcomeCard') : undefined;
  const ui = streamedResult.ui ?? delimitedUi ?? greetingUi;
  const text = greetingUi && !delimitedUi && !streamedResult.ui ? '' : response;

  return { response: ui ? removeDelimitedUi(text) : text, ui };
}

function extractStreamedResult(response: string): AgentInvokeResult {
  if (!response.includes('data:')) return { response };

  let finalResult: unknown;
  for (const line of response.split(/\r?\n/)) {
    if (!line.startsWith('data:')) continue;
    try {
      const event = JSON.parse(line.slice(5).trim()) as { type?: string; result?: unknown };
      if (event.type === 'result') finalResult = event.result;
    } catch {
      // Ignore non-JSON SSE lines and keep searching for the final result event.
    }
  }

  if (typeof finalResult === 'string') {
    try {
      const parsed = JSON.parse(finalResult) as AgentInvokeResult;
      if (parsed && typeof parsed === 'object' && typeof parsed.response === 'string') return parsed;
    } catch {
      return { response: finalResult };
    }
  }
  if (finalResult && typeof finalResult === 'object') return finalResult as AgentInvokeResult;
  return { response };
}

function extractDelimitedUi(response: string): A2uiSurface | undefined {
  const cards = [
    ['ACCOUNT_UI', 'AccountDetailsCard'],
    ['BILLS_UI', 'CustomerBillsCard'],
    ['WELCOME_UI', 'WelcomeCard'],
    ['MSISDN_UI', 'MobileNumberCard'],
  ] as const;

  for (const [name, componentName] of cards) {
    const start = `<<<${name}>>>`;
    const end = `<<<END_${name}>>>`;
    const startIndex = response.indexOf(start);
    const endIndex = response.indexOf(end, startIndex + start.length);
    if (startIndex === -1 || endIndex === -1) continue;
    try {
      const dataModel = JSON.parse(response.slice(startIndex + start.length, endIndex).trim()) as Record<string, unknown>;
      return createCatalogSurface(componentName, dataModel);
    } catch {
      return undefined;
    }
  }
  return undefined;
}

function removeDelimitedUi(response: string): string {
  return response.replace(/<<<[A-Z]+_UI>>>[\s\S]*?<<<END_[A-Z]+_UI>>>/g, '').trim();
}

function isBareGreeting(prompt: string): boolean {
  return /^(hi|hello|hey|good morning|good afternoon|good evening)[!.,\s]*$/i.test(prompt.trim());
}
